import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import NotFoundPage from "@/pages/NotFoundPage";
import Breadcrumbs from "@/components/cms/Breadcrumbs";
import QuoteForm from "@/components/cms/QuoteForm";
import RichBody, { FaqList } from "@/components/cms/RichBody";
import { RelatedProducts, LinkCards, ArticleCard } from "@/components/cms/Related";
import { MapPin, Tag, ArrowRight } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const price = (d) => d.price_type === "fixed" && d.price ? `$${Number(d.price).toFixed(2)}` : d.price_type === "from" && d.price ? `From $${Number(d.price).toFixed(2)}` : "Price on application";

function Hero({ crumbs, title, summary, image, alt, cta = true }) {
  return (
    <section className="bg-steel text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-5 gap-6 items-center">
        <div className="md:col-span-3">
          <Breadcrumbs items={crumbs} />
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold" data-testid="service-h1">{title}</h1>
          {summary && <p className="mt-3 text-slate-200 text-base max-w-2xl">{summary}</p>}
          {cta && <a href="#quote" className="inline-flex mt-5 items-center gap-2 rounded-full bg-accent text-steel font-semibold px-5 py-2.5 hover:bg-amber-500 transition-colors" data-testid="service-quote-cta">Request a quote <ArrowRight size={16} /></a>}
        </div>
        {image && <img src={image} alt={alt || title} className="md:col-span-2 rounded-xl w-full aspect-[4/3] object-cover" />}
      </div>
    </section>
  );
}

function Index() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/services").then((r) => setItems(r.data.items)); }, []);
  return (
    <>
      <Seo title="Technology Services in Auckland | ACP Online" description="Installation, repairs and support for POS, EFTPOS, CCTV, security, networking and retail technology from ACP Online, Auckland." canonical="/services" />
      <Hero crumbs={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]} title="Technology services in Auckland" cta={false} summary="Installation, repairs and ongoing support from the ACP Online team." />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full flex-1">
        {!items ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? <p className="text-slate-600" data-testid="services-empty">Our service pages are coming soon. <Link to="/help/contact" className="text-accent underline">Contact us</Link> in the meantime.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
            {items.map((c) => (
              <Link key={c.id} to={`/services/${c.slug}`} className="group rounded-xl border bg-white p-5 hover:border-accent transition-colors" data-testid={`service-cat-${c.slug}`}>
                {c.image && <img src={c.image} alt={c.image_alt || c.name} className="w-full aspect-[16/9] object-cover rounded-lg mb-3" loading="lazy" />}
                <h2 className="font-bold text-lg text-steel group-hover:text-accent">{c.name}</h2>
                {c.summary && <p className="text-sm text-slate-600 mt-1 line-clamp-3">{c.summary}</p>}
                {c.services?.length > 0 && <p className="text-xs text-slate-500 mt-2">{c.services.map((s) => s.name).join(" · ")}</p>}
              </Link>))}
          </div>)}
      </main>
    </>
  );
}

function Detail({ data }) {
  const d = data.doc;
  const s = useSettings();
  const store = s?.store || {};
  const subs = (data.services || []).map((x) => ({ name: x.name, summary: x.summary, path: `${data.path}/${x.slug}` }));
  const sibs = (data.siblings || []).map((x) => ({ name: x.name, path: `/services/${data.category.slug}/${x.slug}` }));
  return (
    <>
      <Seo title={data.seo_title} description={data.meta_description} canonical={data.canonical} image={d.image} jsonLd={data.jsonld} noindex={!!d.noindex} />
      <Hero crumbs={data.crumbs} title={d.h1 || d.name} summary={d.summary} image={d.image} alt={d.image_alt} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full flex-1 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-10 min-w-0">
          <RichBody html={d.body} testid="service-body" />
          <LinkCards items={subs} title={`${d.name} services`} testid="service-children" />
          <RelatedProducts products={data.products} />
          <FaqList faq={d.faq} />
          {data.articles?.length > 0 && <section><h2 className="font-heading font-bold text-xl text-steel mb-3">Guides &amp; articles</h2>
            <div className="grid sm:grid-cols-2 gap-3">{data.articles.map((a) => <ArticleCard key={a.id} a={a} />)}</div></section>}
        </div>
        <aside className="space-y-4">
          <div className="rounded-xl border bg-white p-5 text-sm space-y-2" data-testid="service-facts">
            <div className="flex items-center gap-2 font-bold text-steel"><Tag size={16} className="text-accent" />{price(d)}</div>
            <div className="flex items-start gap-2 text-slate-600"><MapPin size={16} className="text-accent mt-0.5 shrink-0" />Service area: {(d.areas?.length ? d.areas : ["Auckland"]).join(", ")}</div>
            {store.contact_phone && <a href={`tel:${store.contact_phone}`} className="block text-accent font-semibold">Call {store.contact_phone}</a>}
          </div>
          <QuoteForm serviceId={d.id} serviceName={d.name} cta={d.cta_text} />
          {sibs.length > 0 && <LinkCards items={sibs} title={`More in ${data.category.name}`} testid="service-siblings" />}
        </aside>
      </main>
    </>
  );
}

export default function ServicesPage() {
  const { cat, slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!cat) return;
    setData(null);
    window.scrollTo(0, 0);
    api.get(slug ? `/services/${cat}/${slug}` : `/services/${cat}`)
      .then((r) => (r.data.redirect ? navigate(r.data.redirect, { replace: true }) : setData(r.data)))
      .catch(() => setData(false));
  }, [cat, slug, navigate]);
  if (data === false) return <NotFoundPage />;
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="services-page">
      <Header />
      {!cat ? <Index /> : data ? <Detail data={data} /> : <p className="p-20 text-center text-slate-500 flex-1">Loading…</p>}
      <Footer />
    </div>
  );
}
